import IIdRef from '@src/model/common/IdRef';
import { IFile } from '@src/model/file/File';
import { UserGroupObjectTypeEnum } from '@src/model/usergroup/UserGroupObjectType';
import EntityApiServiceRegistry from '@src/service/api/registry/entity/EntityApiServiceRegistry';
import ListFilterBusinessStore from '@src/service/business/common/listFilterBusinessStore';
import { ICollectionData, ICollectionFetchPayload, IIdDataPayload, ILemonAction, IPayloadAction } from '@src/service/business/common/types';
import { createStaticMessageUserFeedbackError } from '@src/service/business/common/userFeedbackUtils';
import trackAction from '@src/service/util/observable/operators/trackAction';
import { reportCaughtMessage, startGlobalProgress, stopGlobalProgress } from '@src/service/util/observable/operators/userFeedback';
import { Observable } from 'rxjs';
import { catchError, filter, ignoreElements, map, mergeMap } from 'rxjs/operators';

// -
// -------------------- Types&Consts

export interface IReferencedObjectListFilter {
  id: string;
  objectType: IReferencedObjetTypeEnum;
}

export enum IReferencedObjetTypeEnum {
  FILE_RESOURCE = '1',
}

const REFERENCED_OBJECT_LIST_FILTER_NAME = 'REFERENCED_OBJECT_LIST_FILTER_NAME';

// -
// -------------------- Selectors

const getReferencedObjectList = <T>(store: any): ICollectionData<T> => store.referencedObjectList;
const getReferencedObjectListFilter = (store: any): IReferencedObjectListFilter => ListFilterBusinessStore.selectors.getListFilter(store, REFERENCED_OBJECT_LIST_FILTER_NAME);

// -
// -------------------- Actions

const Actions = {
  REFERENCED_OBJECT_LIST_FETCH: 'REFERENCED_OBJECT_LIST_FETCH',
  REFERENCED_OBJECT_CREATE_FETCH: 'REFERENCED_OBJECT_CREATE_FETCH',
  REFERENCED_OBJECT_LIST_LOAD: 'REFERENCED_OBJECT_LIST_LOAD',
  REFERENCED_OBJECT_LIST_CLEAR: 'REFERENCED_OBJECT_LIST_CLEAR',
  REFERENCED_OBJECT_REMOVE: 'REFERENCED_OBJECT_REMOVE',
};

/** Fetch all files in repository */
const fetchReferencedObjectList = (userGroupId: string, listFilter: IReferencedObjectListFilter, page: number, size: number, sort: string[]): IPayloadAction<IIdDataPayload<ICollectionFetchPayload<IReferencedObjectListFilter>>> => {
  return {
    type: Actions.REFERENCED_OBJECT_LIST_FETCH,
    payload: {
      id: userGroupId,
      data: {
        filter: listFilter,
        page,
        size,
        sort,
      },
    },
  };
};

const createReferencedObjectList = (userGroupId: string, objectTypeId: UserGroupObjectTypeEnum, objectRefs: Array<IIdRef<string>>): IPayloadAction<IIdDataPayload<any>> => {
  return {
    type: Actions.REFERENCED_OBJECT_CREATE_FETCH,
    payload: {
      id: userGroupId,
      data: {
        objectType: { id: objectTypeId },
        objects: objectRefs,
      },
    },
  };
};

const removeReferencedObjects = (userGroupId: string, objectTypeId: UserGroupObjectTypeEnum, objectRefs: Array<IIdRef<string>>): IPayloadAction<IIdDataPayload<any>> => {
  return {
    type: Actions.REFERENCED_OBJECT_REMOVE,
    payload: {
      id: userGroupId,
      data: {
        objectType: { id: objectTypeId },
        objects: objectRefs,
      },
    },
  };
};

/** Load files to store */
const loadReferencedObjectList = (data: ICollectionData<IFile>): IPayloadAction<ICollectionData<IFile>> => {
  return {
    type: Actions.REFERENCED_OBJECT_LIST_LOAD,
    payload: data,
  };
};

/** Clear file list */
const clearReferencedObjectList = (): ILemonAction => {
  return {
    type: Actions.REFERENCED_OBJECT_LIST_CLEAR,
  };
};

/** Store list filter to store. */
const storeReferencedObjectListFilter = (listFilter: IReferencedObjectListFilter): ILemonAction => {
  return ListFilterBusinessStore.actions.storeListFilter(REFERENCED_OBJECT_LIST_FILTER_NAME, listFilter);
};

/** Clear list filter from store. */
const clearReferencedObjectListFilter = (): ILemonAction => {
  return ListFilterBusinessStore.actions.clearListFilter(REFERENCED_OBJECT_LIST_FILTER_NAME);
};

// -
// -------------------- Side-effects

const fetchReferencedObjectListEffect = (action$: Observable<IPayloadAction<IIdDataPayload<ICollectionFetchPayload<IReferencedObjectListFilter>>>>, state$: Observable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.REFERENCED_OBJECT_LIST_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const { id, data } = action.payload;

      return EntityApiServiceRegistry.getService('UserGroup')
        .fetchSubentityList(id, 'referencedobject', data)
        .pipe(trackAction(action));
    }),

    stopGlobalProgress(),

    map((data) => {
      return loadReferencedObjectList(data);
    }),

    // tslint:disable-next-line:no-duplicate-string
    reportCaughtMessage((error: any) => createStaticMessageUserFeedbackError('GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.log('Error fetching referenced object list content list', error);
      return o;
    }),
  );
};

const createReferencedObjectListEffect = (action$: Observable<IPayloadAction<IIdDataPayload<IIdDataPayload<Array<IIdRef<string>>>>>>, state$: Observable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.REFERENCED_OBJECT_CREATE_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const { id, data } = action.payload;

      return EntityApiServiceRegistry.getService('UserGroup')
        .createSubobject(id, 'referencedobjects', data)
        .pipe(trackAction(action));
    }),

    stopGlobalProgress(),

    ignoreElements(),

    reportCaughtMessage((error: any) => createStaticMessageUserFeedbackError('GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.log('Error fetching referenced object list content list', error);
      return o;
    }),
  );
};

const removeReferencedObjectsEffect = (action$: Observable<IPayloadAction<IIdDataPayload<IIdDataPayload<Array<IIdRef<string>>>>>>, state$: Observable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.REFERENCED_OBJECT_REMOVE;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const { id, data } = action.payload;

      return EntityApiServiceRegistry.getService('UserGroup')
        .deleteSubobject(id, 'referencedobjects', data)
        .pipe(trackAction(action));
    }),

    stopGlobalProgress(),

    ignoreElements(),

    reportCaughtMessage((error: any) => createStaticMessageUserFeedbackError('GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.log('Error deleting referenced object', error);
      return o;
    }),
  );
};

// -
// -------------------- Reducers

const referencedObjectList = (state: ICollectionData<IFile> | null = null, action: IPayloadAction<ICollectionData<IFile>>) => {
  if (action.type === Actions.REFERENCED_OBJECT_LIST_LOAD) {
    return {
      ...action.payload,
    };
  } else if (action.type === Actions.REFERENCED_OBJECT_LIST_CLEAR) {
    return null;
  }

  return state;
};

// --
// -------------------- Business Store

export const UserGroupReferencedObjectsBusinessStore = {
  actions: {
    fetchReferencedObjectList,
    loadReferencedObjectList,
    clearReferencedObjectList,
    createReferencedObjectList,
    storeReferencedObjectListFilter,
    clearReferencedObjectListFilter,
    removeReferencedObjects,
  },

  selectors: {
    getReferencedObjectList,
    getReferencedObjectListFilter,
  },

  effects: {
    fetchReferencedObjectListEffect,
    createReferencedObjectListEffect,
    removeReferencedObjectsEffect,
  },

  reducers: {
    referencedObjectList,
  },
};

// --
// ----- Exports

export default UserGroupReferencedObjectsBusinessStore;
